import mitt from 'mitt';
import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { useInitialData } from '../localStorageContext';

const MqttContext = createContext(null);
const WebSocketContext = createContext(null);

export const ConnectionProvider = ({ children }) => {
    const { updateInitialData } = useInitialData();
    const mqttWorkerRef = useRef();
    const webSocketWorkerRef = useRef();

    const mqttEventEmitter = useRef(mitt());
    const webSocketEventEmitter = useRef(mitt());

    mqttWorkerRef.current = useMemo(() => {
        const worker = new Worker(new URL('./mqttConnectionManager.js', import.meta.url));
        worker.onmessage = (event) => {
            const { url, action, topic, subscriberComponentIds, payload } = event.data;
            switch (action) {
                case 'ON_MESSAGE':
                    // console.log("ON_MESSAGE: ", event.data);
                    if (Array.isArray(subscriberComponentIds) && subscriberComponentIds.length > 0) {
                        subscriberComponentIds.forEach((subscriberComponentId) => {
                            mqttEventEmitter.current.emit(subscriberComponentId, payload);
                        });
                        break;
                    }
                    // console.log("ON_MESSAGE: No subscribers for topic ", topic);
                    break;
                case 'MQTT_STATE':
                    const mqttStateString = 'MQTT_STATE:' + url;
                    mqttEventEmitter.current.emit(mqttStateString, payload);
                    // console.log("Emitted ", mqttStateString, " with payload: ", payload);
                    break;
                case 'UPDATE_INITIAL_DATA':
                    // console.log("UPDATE_INITIAL_DATA: ", payload);
                    updateInitialData({ broker: payload.broker, data: payload.message });
                    const InitialDataUpdateString = 'INITIAL_DATA_UPDATE:' + url;
                    mqttEventEmitter.current.emit(InitialDataUpdateString, '');
                    break;
                default:
                    console.log("ConnectionManager recieved unknown MQTT action: ", action);
                    break;
            }

            // console.log("_______________________Message from mqttConnectionManager___________________________",
            //     "\nConnectionContext: mqttWorkerRef-onmessage recieved: \nURL: ", url,
            //     "\nAction: ", action,
            //     "\nTopic: ", topic,
            //     "\nSubscriber Component Id's: ", subscriberComponentIds,
            //     "\nPayload: ", payload);
        };
        return worker;
    }, []); //TODO: Hier fehlt eigentlich die updateInitialData in dem Dependency Array, jedoch bleibt bei hinzufuegen dessen der Worker nicht konstant.

    webSocketWorkerRef.current = useMemo(() => {
        const worker = new Worker(new URL('./webSocketConnectionManager.js', import.meta.url));
        worker.onmessage = (event) => {
            const { url, action, type, payload } = event.data;
            switch (action) {
                case 'ON_MESSAGE':
                    const socketOnMessageString = action + url + type;
                    webSocketEventEmitter.current.emit(socketOnMessageString, payload);
                    // console.log(webSocketEventEmitter);
                    // console.log("Emitted ", socketOnMessageString, " with payload: ", payload);
                    break;
                case 'SOCKET_STATE':
                    const socketStateString = 'SOCKET_STATE:' + url;
                    webSocketEventEmitter.current.emit(socketStateString, payload);
                    // console.log("Emitted ", socketStateString, " with payload: ", payload);
                    break;
                default:
                    console.log("ConnectionManager recieved unknown WebSocket action: ", action);
                    break;
            }

            // console.log("_______________________Message from webSocketConnectionManager___________________________",
            //     "\nConnectionContext: webSocketWorkerRef-onmessage recieved: \nURL: ", url,
            //     "\nAction: ", action,
            //     "\nType: ", type,
            //     "\nPayload: ", payload);
        };
        return worker;
    }, []);

    // Methoden zum Senden von Nachrichten an den MQTT-Worker
    const sendToMqttWorker = useCallback((action, payload) => {
        if (mqttWorkerRef.current) {
            mqttWorkerRef.current.postMessage({ action, payload });
        } else {
            console.error('MQTT Worker ist noch nicht initialisiert!');
        }
    }, []);

    // Methoden zum Senden von Nachrichten an den WebSocket-Worker
    const sendToWebSocketWorker = useCallback((action, payload) => {
        if (webSocketWorkerRef.current) {
            webSocketWorkerRef.current.postMessage({ action, payload });
        } else {
            console.error('WebSocket Worker ist noch nicht initialisiert!');
        }
    }, []);

    // MQTT-Methoden
    const mqttConnect = useCallback((config) => sendToMqttWorker('CONNECT', config), [sendToMqttWorker]);
    const mqttReconnect = useCallback((config) => sendToMqttWorker('RECONNECT', config), [sendToMqttWorker]);
    const mqttPublish = useCallback((config) => sendToMqttWorker('PUBLISH', config), [sendToMqttWorker]);
    const mqttSubscribe = useCallback((config) => sendToMqttWorker('SUBSCRIBE', config), [sendToMqttWorker]);
    const mqttUnsubscribe = useCallback((config) => sendToMqttWorker('UNSUBSCRIBE', config), [sendToMqttWorker]);
    const mqttDisconnect = useCallback((config) => sendToMqttWorker('DISCONNECT', config), [sendToMqttWorker]);

    // WebSocket-Methoden
    const webSocketConnect = useCallback((config) => sendToWebSocketWorker('CONNECT', config), [sendToWebSocketWorker]);
    const webSocketReconnect = useCallback((config) => sendToWebSocketWorker('RECONNECT', config), [sendToWebSocketWorker]);
    const webSocketSend = useCallback((config) => sendToWebSocketWorker('SEND', config), [sendToWebSocketWorker]);
    const webSocketSwitchGetData = useCallback((config) => sendToWebSocketWorker('GETDATA', config), [sendToWebSocketWorker]);
    const webSocketDisconnect = useCallback((config) => sendToWebSocketWorker('DISCONNECT', config), [sendToWebSocketWorker]);

    const mqttContextValues = useMemo(() => ({
        mqttConnect,
        mqttReconnect,
        mqttPublish,
        mqttSubscribe,
        mqttUnsubscribe,
        mqttDisconnect,
        eventEmitter: mqttEventEmitter.current
    }), [mqttConnect,
        mqttReconnect,
        mqttPublish,
        mqttSubscribe,
        mqttUnsubscribe,
        mqttDisconnect]);

    const webSocketContextValues = useMemo(() => ({
        webSocketConnect,
        webSocketReconnect,
        webSocketSend,
        webSocketSwitchGetData,
        webSocketDisconnect,
        eventEmitter: webSocketEventEmitter.current
    }), [webSocketConnect,
        webSocketReconnect,
        webSocketSend,
        webSocketSwitchGetData,
        webSocketDisconnect]);

    return (
        <MqttContext.Provider value={mqttContextValues}>
            <WebSocketContext.Provider value={webSocketContextValues}>
                {children}
            </WebSocketContext.Provider>
        </MqttContext.Provider>
    );
};

export const useMqttConnection = () => useContext(MqttContext);
export const useWebSocketConnection = () => useContext(WebSocketContext);